import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

interface AnimationTarget {
  current: HTMLElement | null;
}

interface CardRef {
  current: (HTMLDivElement | null)[];
}

/**
 * Animates the heading with an upward fade-in motion
 */
export const animateHeading = (headingRef: AnimationTarget) => {
  if (!headingRef.current) return;

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: headingRef.current,
      start: "top 80%",
      once: true,
    },
  });

  tl.fromTo(
    headingRef.current,
    { y: 30, opacity: 0 },
    { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }
  );
};

/**
 * Animates the product cards with optimized performance
 */
export const animateCards = (
  sectionRef: AnimationTarget,
  cardsRef: CardRef
) => {
  const cards = cardsRef.current.filter(Boolean);
  if (!cards.length || !sectionRef.current) return;

  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: sectionRef.current,
      start: "top 70%",
      once: true,
    },
  });

  tl.fromTo(
    cards,
    { y: 50, autoAlpha: 0 },
    { y: 0, autoAlpha: 1, duration: 0.8, stagger: 0.15, ease: "power3.out" }
  );
};

/**
 * Optimized hover effects with better performance
 */
export const addCardHoverEffects = (cardsRef: CardRef) => {
  const cards = cardsRef.current.filter(Boolean) as HTMLDivElement[];
  const cleanupFunctions: (() => void)[] = [];

  cards.forEach((card) => {
    // Use CSS transforms for better performance
    const handleMouseEnter = () => {
      gsap.to(card, {
        scale: 1.02,
        boxShadow:
          "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        duration: 0.3,
        ease: "power2.out",
      });
    };

    const handleMouseLeave = () => {
      gsap.to(card, {
        scale: 1,
        boxShadow:
          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
        duration: 0.3,
        ease: "power2.out",
      });
    };

    card.addEventListener("mouseenter", handleMouseEnter, { passive: true });
    card.addEventListener("mouseleave", handleMouseLeave, { passive: true });

    cleanupFunctions.push(() => {
      card.removeEventListener("mouseenter", handleMouseEnter);
      card.removeEventListener("mouseleave", handleMouseLeave);
    });
  });

  return () => cleanupFunctions.forEach((cleanup) => cleanup());
};

/**
 * Optimized image fade-in
 */
export const fadeInImage = (imageRef: HTMLImageElement, index: number) => {
  if (parseFloat(imageRef.style.opacity) === 0) {
    gsap.to(imageRef, {
      opacity: 1,
      duration: 0.5,
      delay: 0.1 * Math.min(index, 3), // Cap delay at 4 items
      ease: "power2.out",
    });
  }
};

/**
 * Clean up all ScrollTrigger instances
 */
export const cleanupScrollTriggers = () => {
  ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
  ScrollTrigger.refresh();
};
